import React, { useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";

const MaximizeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export const METRIC_DEFINITIONS = {
  "Average Time Between Assessments": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the average number of days between consecutive completed assessments for each assessment type (e.g., PHQ-9, GAD-7, ISI, PROMIS Pain Interference 8a). This metric helps evaluate how consistently patients complete their scheduled assessments and identifies assessment adherence patterns across different assessment types."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with at least two completed assessments of the selected assessment type during the reporting period.",
          "Only completed assessments are included.",
          "Calculated independently for each assessment type."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Select the reporting period.",
          "Retrieve all completed assessments for the selected assessment type within the reporting period.",
          "Sort each patient's assessments chronologically.",
          "Calculate the number of days between every pair of consecutive completed assessments.",
          "Calculate the average gap for each patient.",
          "Calculate the overall average by averaging the patient-level averages."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Gap (Days) = Current Assessment Date − Previous Assessment Date",
          "Patient Average Gap = Total Gap Days ÷ Number of Assessment Gaps",
          "Average Time Between Assessments = Sum of Patient Average Gaps ÷ Total Eligible Patients"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Lower values indicate patients are completing assessments more consistently.",
          "Higher values indicate longer intervals between assessments and may highlight reduced assessment adherence or delayed patient engagement.",
          "Useful for comparing adherence across different assessment types."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients with only one completed assessment.",
          "Draft, incomplete, or cancelled assessments.",
          "Assessments outside the selected reporting period.",
          "Missing or invalid assessment dates."
        ]
      }
    ]
  },
  "Assessment Completion Rate": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the percentage of expected assessments that were completed for each assessment type during the selected reporting period. The metric evaluates adherence to the configured assessment plan by comparing scheduled assessment occurrences with completed assessments."
        ]
      },
      {
        heading: "Eligible Assessment Plans",
        items: [
          "Assessment plans that overlap the selected reporting period.",
          "Supports individual assessment types or all assessment types.",
          "Expected assessments are derived from the configured assessment schedule."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify all assessment plans applicable during the reporting period.",
          {
            text: "Calculate the expected assessment occurrences using:",
            subItems: [
              "Assessment Start Date",
              "Assessment End Date",
              "Assessment Frequency"
            ]
          },
          "Count completed assessments corresponding to those expected occurrences.",
          "A completed assessment counts even if completed after the scheduled due date.",
          "Calculate the completion rate independently for each assessment type."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Expected Assessments = Number of Scheduled Assessment Occurrences within the Reporting Period",
          "Assessment Completion Rate (%) = (Completed Assessments ÷ Expected Assessments) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher completion rates indicate better adherence to prescribed assessment schedules.",
          "Lower completion rates may identify assessment types requiring patient follow-up or workflow improvements.",
          "Enables comparison of adherence across different assessment types."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Assessment plans outside the reporting period.",
          "Assessment occurrences outside the selected reporting period.",
          "Draft, cancelled, or incomplete assessments.",
          "Duplicate assessment completions for the same scheduled occurrence."
        ]
      }
    ]
  },
  "Patients Overdue For Reassessment": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Identifies assessment types that are currently overdue for each patient and measures how long they have been overdue. This metric helps prioritize patient follow-up and monitor assessment compliance across the organization."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with an active assessment plan.",
          "Calculated independently for every patient and assessment type.",
          "Supports all configured assessment types (e.g., PHQ-9, GAD-7, ISI, PROMIS Pain Interference 8a, Columbia, etc.)."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify the latest completed assessment for each patient and assessment type.",
          "Calculate the next expected due date using the configured assessment frequency.",
          "If no assessment has been completed, use the configured assessment start date as the first due date.",
          "Compare the next due date with the selected reference date.",
          {
            text: "Classify the assessment as:",
            subItems: [
              "Not Overdue: Next Due Date is on or after the reference date.",
              "Overdue: Next Due Date is before the reference date."
            ]
          },
          {
            text: "For overdue assessments, calculate the number of overdue days and assign an overdue bucket:",
            subItems: [
              "1–7 Days: Recently Overdue",
              "8–14 Days: Moderately Overdue",
              "15–30 Days: Significantly Overdue",
              ">30 Days: Critically Overdue"
            ]
          }
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Next Due Date = Last Completed Assessment Date + Configured Assessment Frequency",
          "Overdue Days = Reference Date − Next Due Date",
          "Overdue Assessments = Count of Assessments where Next Due Date < Reference Date"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "A higher number of overdue assessments indicates lower adherence to scheduled assessments.",
          "Overdue duration highlights patients requiring immediate clinical follow-up.",
          "Duration buckets help prioritize outreach based on urgency.",
          "Enables comparison of overdue trends across assessment types."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Assessment types without a configured schedule.",
          "Cancelled or inactive assessment plans.",
          "Assessments not yet due as of the selected reference date.",
          "Invalid assessment dates or missing scheduling information."
        ]
      }
    ]
  },
  "Assessment Improvement Trend": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the percentage of patients achieving a clinically meaningful improvement (≥5-point reduction from baseline) across supported assessment types over time. This metric helps monitor treatment effectiveness and compare improvement trends for Depression, Anxiety, Pain, and Insomnia."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with a baseline assessment and at least one follow-up assessment.",
          "Only completed assessments are considered.",
          "PHQ-9 and GAD-7: Assessments from both Precise and Healthie.",
          "PROMIS Pain Interference 8a and ISI: Assessments from Precise only."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify the first completed assessment within the reporting period as the baseline.",
          "Keep the baseline fixed throughout the reporting period.",
          "For each calendar month, select the latest completed assessment.",
          "Calculate: Improvement = Baseline Score − Latest Monthly Score",
          {
            text: "Classify patients as:",
            subItems: [
              "Improved: Improvement ≥5 points",
              "Not Improved: Improvement <5 points"
            ]
          },
          "Patients without an assessment in a month are excluded only for that month's calculation."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Monthly Improvement Rate (%) = (Patients with Improvement ≥5 Points ÷ Eligible Patients with an Assessment in that Month) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher improvement rates indicate more patients are achieving clinically meaningful symptom reduction.",
          "Comparing trends across assessment types helps evaluate treatment effectiveness across different clinical conditions."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients with only one completed assessment.",
          "Missing baseline or follow-up assessments.",
          "Draft, incomplete, or invalid assessments.",
          "Missing or invalid assessment scores."
        ]
      }
    ]
  },
  "Assessment Remission Trend": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the percentage of patients achieving clinical remission across supported assessment types over time. This metric helps monitor recovery trends for Depression, Anxiety, Pain, and Insomnia."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with a baseline assessment and at least one follow-up assessment.",
          "Only completed assessments are considered.",
          "PHQ-9 and GAD-7: Assessments from both Precise and Healthie.",
          "PROMIS Pain Interference 8a and ISI: Assessments from Precise only."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify the baseline assessment.",
          "For each calendar month, select the latest completed assessment.",
          "A patient is considered In Remission if: Latest Monthly Score < 5 (PHQ-9/GAD-7), < 8 (ISI), or < 10 (PROMIS Pain-8).",
          "Patients without an assessment in a month are excluded only for that month's calculation."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Monthly Remission Rate (%) = (Patients in Remission ÷ Eligible Patients with an Assessment in that Month) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher remission rates indicate a greater proportion of patients have minimal or no clinically significant symptoms.",
          "Comparing remission trends across assessment types helps evaluate long-term treatment outcomes."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients with only one completed assessment.",
          "Missing baseline or follow-up assessments.",
          "Draft, incomplete, or invalid assessments.",
          "Missing or invalid assessment scores."
        ]
      }
    ]
  },
  "Insomnia and Pain Severity Improvement Trend": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Displays the average improvement in patient assessment scores over time for the selected outcome assessment. The metric helps monitor whether patients' pain or insomnia symptoms are improving throughout the course of treatment by comparing monthly scores against each patient's baseline assessment."
        ]
      },
      {
        heading: "Supported Assessment Types",
        items: [
          "PROMIS Pain Interference 8a",
          "Insomnia Severity Index (ISI)",
          "Each assessment type is calculated independently and displayed as a separate trend."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with at least two completed assessments of the selected assessment type.",
          "Only completed assessments within the selected reporting period are considered.",
          "Patients with only one completed assessment are excluded."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify the patient's first completed assessment within the selected reporting period as the Baseline Assessment.",
          "The baseline remains fixed for the entire reporting period.",
          "For each calendar month, identify the patient's latest completed assessment.",
          "If multiple assessments exist within a month, only the latest assessment is considered.",
          "Patients without an assessment in a particular month are excluded from that month's calculation.",
          "Calculate individual patient improvement using: Improvement = Baseline Score − Latest Monthly Score",
          "Calculate the monthly average improvement across all eligible patients."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Average Monthly Improvement = Sum of Improvement Values for the Month ÷ Number of Eligible Patients with an Assessment in that Month"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher positive values indicate greater improvement in symptoms.",
          "A value of 0 indicates no change from baseline.",
          "Negative values indicate worsening symptoms.",
          "Trends can be compared across Pain and Insomnia assessments to evaluate treatment effectiveness over time."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients with only one completed assessment.",
          "Draft, incomplete, or cancelled assessments.",
          "Patients without a monthly assessment (excluded only for that month).",
          "Missing or invalid assessment scores."
        ]
      }
    ]
  },
  "Outcome by Cohort (New vs Existing Patients)": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Compares clinical outcomes between newly registered and existing patients to evaluate whether treatment outcomes differ based on how long patients have been receiving care."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with a baseline assessment and at least one follow-up assessment.",
          "PHQ-9 and GAD-7: Assessments from both Precise and Healthie.",
          "Other supported assessments use their configured data source."
        ]
      },
      {
        heading: "Cohort Definition",
        items: [
          "New Patients: Registered during the selected reporting period.",
          "Existing Patients: Registered before the selected reporting period."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Assign each patient to either the New or Existing cohort.",
          "Compare the baseline and latest assessment.",
          "Calculate: Improvement = Baseline Score − Latest Score",
          {
            text: "Classify outcomes as:",
            subItems: [
              "Remission (Latest Score <5)",
              "Improved (≥5-point improvement)",
              "Partial Improvement (1–4-point improvement)",
              "No Improvement/Worsened"
            ]
          }
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Outcome (%) = (Patients in Outcome Category ÷ Total Patients in the Cohort) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Helps compare recovery outcomes between newly onboarded and long-term patients.",
          "Supports evaluation of onboarding effectiveness and long-term care outcomes."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients without baseline or follow-up assessments.",
          "Draft or invalid assessments.",
          "Missing assessment scores."
        ]
      }
    ]
  },
  "Outcome by Engagement Level": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Compares clinical outcomes across different patient engagement levels to determine whether higher engagement is associated with better health outcomes."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with a baseline assessment and at least one follow-up assessment.",
          "At least one qualifying engagement activity during the reporting period."
        ]
      },
      {
        heading: "Engagement Activities",
        items: [
          "Assessment Completed",
          "Check-in Completed",
          "Journal Entry",
          "Chat Message (once per day)",
          "Profile Update (once per reporting period)",
          {
            text: "Patients are grouped into:",
            subItems: [
              "Low Engagement",
              "Medium Engagement",
              "High Engagement"
            ]
          }
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Calculate an Engagement Score using qualifying activities.",
          "Categorize patients into engagement levels.",
          "Compare baseline and latest assessments.",
          {
            text: "Classify outcomes as:",
            subItems: [
              "Remission",
              "Improved",
              "Partial Improvement",
              "No Improvement/Worsened"
            ]
          }
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Outcome (%) = (Patients in Outcome Category ÷ Total Patients in the Engagement Level) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Helps determine whether increased platform engagement is associated with improved clinical outcomes.",
          "Supports evaluation of engagement initiatives and digital interventions."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients without baseline or follow-up assessments.",
          "Patients without measurable engagement.",
          "Passive or read-only interactions."
        ]
      }
    ]
  },
  "Outcome by Age Group": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Compares clinical outcomes across different age groups to identify demographic trends in patient recovery and treatment effectiveness."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with a baseline assessment and at least one follow-up assessment.",
          "Valid patient age available.",
          "Supported assessments use their configured data sources."
        ]
      },
      {
        heading: "Age Groups",
        items: [
          "Patients are grouped into configurable age ranges (e.g., 18–25, 26–35, 36–45, 46–60, 60+)."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Assign patients to an age group.",
          "Compare baseline and latest assessments.",
          "Calculate: Improvement = Baseline Score − Latest Score",
          {
            text: "Classify outcomes as:",
            subItems: [
              "Remission",
              "Improved",
              "Partial Improvement",
              "No Improvement/Worsened"
            ]
          }
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Outcome (%) = (Patients in Outcome Category ÷ Total Patients in the Age Group) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Identifies age groups achieving stronger or weaker treatment outcomes.",
          "Helps support age-specific care planning and intervention strategies."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Missing age information.",
          "Missing baseline or follow-up assessments.",
          "Invalid assessment scores."
        ]
      }
    ]
  },
  "Outcome by Gender": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Compares clinical outcomes across gender groups to identify differences in treatment effectiveness and recovery."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with baseline and follow-up assessments.",
          "Valid gender information available."
        ]
      },
      {
        heading: "Gender Groups",
        items: [
          "Male",
          "Female",
          "Other / Unknown"
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Group patients by gender.",
          "Compare baseline and latest assessments.",
          {
            text: "Classify outcomes as:",
            subItems: [
              "Remission",
              "Improved",
              "Partial Improvement",
              "No Improvement/Worsened"
            ]
          }
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Outcome (%) = (Patients in Outcome Category ÷ Total Patients in the Gender Group) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Enables comparison of treatment outcomes across gender groups.",
          "Supports demographic analysis and clinical planning."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Missing gender information.",
          "Missing baseline or follow-up assessments.",
          "Invalid assessment scores."
        ]
      }
    ]
  },
  "Outcome by Comorbidity": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Compares clinical outcomes across patients with different comorbid conditions to understand how additional health conditions influence treatment outcomes."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with baseline and follow-up assessments.",
          "At least one documented comorbidity."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Group patients by documented comorbidity.",
          "A patient may belong to multiple comorbidity groups.",
          "Compare baseline and latest assessments.",
          {
            text: "Classify outcomes as:",
            subItems: [
              "Remission",
              "Improved",
              "Partial Improvement",
              "No Improvement/Worsened"
            ]
          }
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Outcome (%) = (Patients in Outcome Category ÷ Total Patients in the Comorbidity Group) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Identifies which comorbidity groups demonstrate stronger or weaker treatment outcomes.",
          "Supports targeted clinical interventions for high-risk populations."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients without documented comorbidities.",
          "Missing baseline or follow-up assessments.",
          "Invalid assessment scores."
        ]
      }
    ]
  },
  "Patient Distribution by Comorbidity Group": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Shows how the patient population is distributed across different comorbidity groups, helping identify the prevalence of clinical conditions within the organization."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with one or more documented comorbidities.",
          "Active patient records matching the selected filters."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify all documented comorbidities.",
          "Group patients by comorbidity.",
          "Count unique patients in each group.",
          "Patients with multiple comorbidities may appear in multiple groups."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Patient Distribution (%) = (Unique Patients in Comorbidity Group ÷ Total Patients with Documented Comorbidities) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Highlights the prevalence of different clinical conditions.",
          "Supports resource planning and population health analysis."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients without documented comorbidities.",
          "Inactive or excluded patient records."
        ]
      }
    ]
  },
  "Population Health: Outcome by Comorbidity": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Provides a population-level view of clinical outcomes across comorbidity groups, helping identify which patient populations respond best to treatment and which may require additional clinical support."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients with baseline and follow-up assessments.",
          "At least one documented comorbidity.",
          "Supported assessments use their configured data sources."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Group patients by documented comorbidity.",
          "Compare baseline and latest assessments.",
          {
            text: "Classify outcomes as:",
            subItems: [
              "Remission",
              "Improved",
              "Partial Improvement",
              "No Improvement/Worsened"
            ]
          },
          "Calculate the outcome distribution for each comorbidity group."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Outcome (%) = (Patients in Outcome Category ÷ Total Patients in the Comorbidity Group) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Enables comparison of treatment effectiveness across different patient populations.",
          "Helps identify comorbidity groups with higher remission rates or poorer outcomes, supporting population health planning and targeted interventions."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients without documented comorbidities.",
          "Missing baseline or follow-up assessments.",
          "Invalid assessment scores."
        ]
      }
    ]
  },
  "Feature Usage": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the percentage of active patients who use key application features during the selected reporting period. This metric evaluates feature adoption based on meaningful user actions rather than screen views."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients who performed at least one qualifying activity during the reporting period (Active Patients)."
        ]
      },
      {
        heading: "Features Measured",
        items: [
          "Assessment",
          "Check-in",
          "Journal",
          "Chat",
          "Profile",
          "Notification Settings"
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify all Active Patients.",
          "For each feature, count unique patients who performed the corresponding meaningful action.",
          "A patient is counted only once per feature, regardless of usage frequency.",
          "A patient may contribute to multiple features."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Feature Usage (%) = (Unique Patients Using the Feature ÷ Total Active Patients) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher usage indicates greater adoption of a feature.",
          "Lower usage may highlight features requiring usability improvements or increased awareness.",
          "Enables comparison of adoption across application features."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Home Screen Views",
          "Articles or Videos Viewed",
          "Guided Journey Viewed",
          "Screen Views",
          "Any passive or read-only interaction."
        ]
      }
    ]
  },
  "Daily Active Users (DAU)": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the number of unique patients who perform at least one meaningful activity on a given calendar day. This metric provides insight into day-to-day patient engagement and platform usage."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients who perform at least one qualifying activity during the selected day.",
          "Each patient is counted only once per day, regardless of the number of activities performed."
        ]
      },
      {
        heading: "Qualifying Activities",
        items: [
          {
            text: "The following activities are considered meaningful engagement:",
            subItems: [
              "Assessment Submitted",
              "Check-in Submitted",
              "Journal Entry Submitted",
              "Chat Message Sent"
            ]
          },
          {
            text: "The following activities are not considered:",
            subItems: [
              "Application Logins",
              "Home Screen Views",
              "Article or Video Views",
              "Resource Views",
              "Notification Opens",
              "Any passive or read-only interaction"
            ]
          }
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Select the reporting period.",
          "For each calendar day, identify patients who performed at least one qualifying activity.",
          "Count each patient only once for that day."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "DAU = Count of Unique Patients with at Least One Qualifying Activity on the Day"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher DAU indicates stronger daily engagement.",
          "Trends can help identify peak usage days and measure the impact of engagement initiatives."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Passive or read-only interactions.",
          "Duplicate activities performed by the same patient on the same day."
        ]
      }
    ]
  },
  "Weekly Active Users (WAU)": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the number of unique patients who perform at least one meaningful activity during a calendar week. This metric reflects sustained engagement over a weekly period."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients who perform at least one qualifying activity during the selected calendar week.",
          "Each patient is counted only once per week."
        ]
      },
      {
        heading: "Qualifying Activities",
        items: [
          {
            text: "The following activities are considered meaningful engagement:",
            subItems: [
              "Assessment Submitted",
              "Check-in Submitted",
              "Journal Entry Submitted",
              "Chat Message Sent"
            ]
          },
          {
            text: "The following activities are not considered:",
            subItems: [
              "Application Logins",
              "Home Screen Views",
              "Article or Video Views",
              "Resource Views",
              "Notification Opens",
              "Any passive or read-only interaction"
            ]
          }
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Select the reporting period.",
          "For each calendar week, identify patients who performed at least one qualifying activity.",
          "Count each patient only once within the week, regardless of the number of activities."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "WAU = Count of Unique Patients with at Least One Qualifying Activity during the Calendar Week"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher WAU indicates consistent weekly engagement.",
          "Comparing WAU over time helps monitor patient retention and recurring platform usage."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Passive or read-only interactions.",
          "Duplicate activities performed by the same patient during the same week."
        ]
      }
    ]
  },
  "Monthly Active Users (MAU)": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the number of unique patients who perform at least one meaningful activity during a calendar month. This metric provides a view of long-term patient engagement and platform adoption."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients who perform at least one qualifying activity during the selected calendar month.",
          "Each patient is counted only once per month."
        ]
      },
      {
        heading: "Qualifying Activities",
        items: [
          {
            text: "The following activities are considered meaningful engagement:",
            subItems: [
              "Assessment Submitted",
              "Check-in Submitted",
              "Journal Entry Submitted",
              "Chat Message Sent"
            ]
          },
          {
            text: "The following activities are not considered:",
            subItems: [
              "Application Logins",
              "Home Screen Views",
              "Article or Video Views",
              "Resource Views",
              "Notification Opens",
              "Any passive or read-only interaction"
            ]
          }
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Select the reporting period.",
          "For each calendar month, identify patients who performed at least one qualifying activity.",
          "Count each patient only once within the month."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "MAU = Count of Unique Patients with at Least One Qualifying Activity during the Calendar Month"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher MAU indicates better long-term platform adoption and sustained patient engagement.",
          "Comparing MAU across months helps identify growth or decline in overall platform usage."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Passive or read-only interactions.",
          "Duplicate activities performed by the same patient during the same month."
        ]
      }
    ]
  },
  "Active vs Inactive Patients": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Classifies patients as Active or Inactive based on whether they performed at least one meaningful activity during the selected activity window. This metric helps identify patients actively participating in their care and those who may require re-engagement."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "All patients matching the selected dashboard filters.",
          "Activity is evaluated only within the selected reporting period."
        ]
      },
      {
        heading: "Qualifying Activities",
        items: [
          {
            text: "The following activities are considered meaningful engagement:",
            subItems: [
              "Assessment Submitted",
              "Check-in Submitted",
              "Journal Entry Submitted",
              "Chat Message Sent"
            ]
          },
          {
            text: "The following activities are not considered:",
            subItems: [
              "Home Screen Views",
              "Insights Viewed",
              "Articles or Videos Viewed",
              "Guided Journey Viewed",
              "Daily Tips Viewed",
              "Profile Updates",
              "Notification Settings Updates",
              "Any passive or read-only interaction"
            ]
          }
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Select the activity window.",
          "Determine whether each patient performed at least one qualifying activity.",
          {
            text: "Classify patients as:",
            subItems: [
              "Active: At least one qualifying activity.",
              "Inactive: No qualifying activities."
            ]
          },
          "Each patient is counted only once."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Active Patients (%) = (Active Patients ÷ Total Patients) × 100",
          "Inactive Patients (%) = (Inactive Patients ÷ Total Patients) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher Active Patient percentages indicate stronger patient engagement.",
          "Higher Inactive Patient percentages may indicate patients requiring reminders or follow-up.",
          "Useful for monitoring overall platform engagement."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Passive or read-only interactions.",
          "Duplicate qualifying activities."
        ]
      }
    ]
  },
  "App Retention (7 days, 30 days, 60 days, 90 days)": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the percentage of patients from a selected registration cohort who continue to actively engage with the application after onboarding. This metric evaluates long-term patient retention and engagement."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients whose Registration Date falls within the selected registration cohort.",
          "Each patient is included only once."
        ]
      },
      {
        heading: "Qualifying Activities",
        items: [
          {
            text: "The following activities are considered meaningful engagement:",
            subItems: [
              "Assessment Completed",
              "Check-in Completed",
              "Journal Entry Completed",
              "Chat Message Sent",
              "Profile Updated",
              "Notification Settings Updated"
            ]
          },
          {
            text: "The following activities are not considered:",
            subItems: [
              "Home Screen Views",
              "Content Views",
              "Articles or Videos Viewed",
              "Insights Viewed",
              "Any passive navigation or read-only interaction"
            ]
          }
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify each patient's Registration Date.",
          {
            text: "Calculate retention milestones:",
            subItems: [
              "Day 7",
              "Day 30",
              "Day 60",
              "Day 90"
            ]
          },
          "Determine whether the patient performed at least one qualifying activity on or after each milestone.",
          "Patients retained at a later milestone automatically qualify for all earlier milestones."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Retention Rate (%) = (Retained Patients ÷ Total Registered Patients in the Cohort) × 100",
          "Calculated independently for Day 7, Day 30, Day 60, and Day 90."
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher retention indicates sustained patient engagement.",
          "Comparing milestones helps identify when patient drop-off occurs.",
          "Useful for evaluating onboarding and long-term engagement initiatives."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients outside the selected registration cohort.",
          "Activities before the applicable retention milestone.",
          "Passive or read-only interactions."
        ]
      }
    ]
  },
  "Time To First Assessment": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the time taken for newly registered patients to complete their first assessment. This metric evaluates the effectiveness of the onboarding process by tracking how quickly patients become clinically engaged after registration."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients whose Registration Date falls within the selected Registration Date filter (registration cohort).",
          "Only the earliest completed assessment for each patient is considered.",
          "Once a patient is included in the cohort, the first completed assessment is considered even if it occurs outside the selected reporting period."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify each patient's Registration Date & Time.",
          "Identify the patient's first completed assessment.",
          "Calculate the elapsed time between registration and the first completed assessment.",
          "If multiple completed assessments exist, only the earliest assessment is considered.",
          {
            text: "Classify each patient into one of the following time buckets:",
            subItems: [
              "≤ 10 Minutes",
              "10 Minutes – 1 Hour",
              "1 Hour – 24 Hours",
              "24 Hours – 3 Days",
              "3 Days – 7 Days",
              "> 7 Days",
              "Never Completed"
            ]
          }
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Time to First Assessment = First Completed Assessment Timestamp − Registration Timestamp",
          {
            text: "For each time bucket:",
            subItems: [
              "Completion Rate (%) = (Patients in Time Bucket ÷ Total Registered Patients in the Selected Registration Cohort) × 100"
            ]
          }
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "Higher percentages in shorter time buckets indicate a more effective onboarding experience and faster patient activation.",
          "Higher percentages in longer time buckets or the Never Completed category may indicate onboarding friction or delayed clinical engagement.",
          "The distribution helps identify where patients experience the greatest delays after registration."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients registered outside the selected registration cohort.",
          "Draft, cancelled, or incomplete assessments.",
          "Invalid or missing registration or assessment timestamps.",
          "Only the first completed assessment is considered; subsequent assessments are ignored for this metric."
        ]
      }
    ]
  },
  "New Patient Onboarding Rate": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the percentage of newly registered patients who complete their first assessment within 7 calendar days of registration. This metric evaluates the effectiveness of the onboarding process and how successfully new patients become clinically engaged."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients whose Registration Date falls within the selected Registration Date filter (registration cohort).",
          "Each patient is included only once in the selected cohort.",
          "The first completed assessment is considered even if it occurs outside the selected reporting period, provided it falls within the patient's 7-day onboarding window."
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify each patient's Registration Date.",
          "Identify the patient's first completed assessment.",
          "Calculate the number of days between registration and the first completed assessment.",
          {
            text: "Classify each patient as:",
            subItems: [
              "Onboarded: First assessment completed within 7 calendar days of registration.",
              "Not Onboarded: No assessment completed within 7 days or no assessment completed."
            ]
          },
          "Group patients by their registration cohort (Day, Week, or Month) to calculate onboarding trends."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Onboarding Rate (%) = (Number of Onboarded Patients ÷ Total Registered Patients in the Selected Registration Cohort) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "A higher onboarding rate indicates that more patients are completing their initial assessment soon after registration.",
          "Lower onboarding rates may indicate friction in the registration or assessment journey and opportunities to improve patient activation.",
          "Monitoring onboarding trends over time helps evaluate the effectiveness of onboarding initiatives."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients registered outside the selected registration cohort.",
          "Draft, cancelled, or incomplete assessments.",
          "Patients whose first completed assessment occurs more than 7 calendar days after registration."
        ]
      }
    ]
  },
  "Drop Off Rate After Onboarding (7 days)": {
    sections: [
      {
        heading: "Definition",
        items: [
          "Measures the percentage of newly registered patients who do not perform any meaningful engagement activity within 7 calendar days of registration. This metric helps identify early patient disengagement during the onboarding period."
        ]
      },
      {
        heading: "Eligible Patients",
        items: [
          "Patients whose Registration Date falls within the selected Registration Date filter (registration cohort).",
          "Each patient is included only once in the selected cohort.",
          "Activities performed outside the 7-day onboarding window do not affect the calculation."
        ]
      },
      {
        heading: "Qualifying Activities",
        items: [
          {
            text: "The following activities are considered meaningful engagement:",
            subItems: [
              "Assessment Completed",
              "Check-in Completed",
              "Journal Entry Completed"
            ]
          },
          {
            text: "The following activities are not considered:",
            subItems: [
              "Chat Messages",
              "Profile Updates",
              "Notification Settings Updates",
              "Content Views",
              "Article or Video Views",
              "Home Screen Views",
              "Insights Viewed",
              "Any passive or read-only interactions"
            ]
          }
        ]
      },
      {
        heading: "Calculation Logic",
        items: [
          "Identify each patient's Registration Date.",
          "Evaluate the first 7 calendar days following registration.",
          "Determine whether the patient completed at least one qualifying activity during this period.",
          {
            text: "Classify each patient as:",
            subItems: [
              "Engaged: Completed at least one Assessment, Check-in, or Journal Entry within 7 days.",
              "Dropped Off: Did not complete any qualifying activity within 7 days."
            ]
          },
          "Patients whose first qualifying activity occurs after the 7-day window are still classified as Dropped Off."
        ]
      },
      {
        heading: "Final Calculation",
        items: [
          "Drop-off Rate (%) = (Number of Dropped Off Patients ÷ Total Registered Patients in the Selected Registration Cohort) × 100"
        ]
      },
      {
        heading: "Interpretation",
        items: [
          "A lower drop-off rate indicates stronger early patient engagement after registration.",
          "A higher drop-off rate suggests that patients are disengaging before completing meaningful clinical activities and may require targeted onboarding improvements or re-engagement strategies.",
          "This metric complements the Onboarding Rate by highlighting patients who fail to engage during the critical first week."
        ]
      },
      {
        heading: "Excluded",
        items: [
          "Patients registered outside the selected registration cohort.",
          "Passive or read-only interactions.",
          "Activities performed after the patient's initial 7-day onboarding window.",
          "Duplicate qualifying activities (a patient is counted only once)."
        ]
      }
    ]
  }
};

const ChartCard = ({ title, subtitle, children, minWidth }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Match title to METRIC_DEFINITIONS
  const matchedKey = title && Object.keys(METRIC_DEFINITIONS).find(
    k => k.toLowerCase() === title.toLowerCase() || title.toLowerCase().includes(k.toLowerCase())
  );
  const metricInfo = matchedKey ? METRIC_DEFINITIONS[matchedKey] : null;

  return (
    <>
      <div className="analytics-chart-card">
        <div className="analytics-chart-header">
          <div className="analytics-chart-title-group" style={{ display: "flex", alignItems: "center" }}>
            {title && <h6 className="analytics-chart-title mb-0">{title}</h6>}
            {metricInfo && (
              <button
                type="button"
                className="analytics-info-btn"
                onClick={() => setShowInfoModal(true)}
                title={title}
              >
                <InfoIcon />
              </button>
            )}
          </div>
          <div className="analytics-chart-header-right">
            {subtitle && (
              <span className="analytics-chart-period">{subtitle}</span>
            )}
            <button
              type="button"
              className="analytics-chart-expand-btn"
              onClick={() => setIsExpanded(true)}
              aria-label="Expand chart to fullscreen"
              title="Expand to fullscreen"
            >
              <MaximizeIcon />
            </button>
          </div>
        </div>
        <div className="analytics-chart-scroll-wrapper">
          <div
            className="analytics-chart-content"
            style={minWidth ? { minWidth } : undefined}
          >
            {children}
          </div>
        </div>
      </div>

      {/* Fullscreen Expand Modal */}
      <Modal
        isOpen={isExpanded}
        toggle={() => setIsExpanded(false)}
        size="xl"
        className="analytics-fullscreen-modal"
        centered
      >
        <ModalHeader
          toggle={() => setIsExpanded(false)}
          className="analytics-fullscreen-header"
        >
          {title || "Chart"}
        </ModalHeader>
        <ModalBody className="analytics-fullscreen-body">
          {subtitle && (
            <p className="analytics-chart-subtitle mb-2">{subtitle}</p>
          )}
          <div className="analytics-fullscreen-scroll-wrapper">
            <div className="analytics-fullscreen-chart-content">
              {children}
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Metric Info Definition Modal matching Executive Level tab styling */}
      {metricInfo && (
        <Modal
          isOpen={showInfoModal}
          toggle={() => setShowInfoModal(false)}
          className="analytics-info-modal"
          centered
        >
          <ModalHeader
            toggle={() => setShowInfoModal(false)}
            className="analytics-info-modal-header"
          >
            {title}
          </ModalHeader>
          <ModalBody className="analytics-info-modal-body">
            {metricInfo.sections ? (
              metricInfo.sections.map((section, idx) => (
                <div key={idx} className="analytics-info-section">
                  <h6 className="analytics-info-section-heading">
                    {section.heading}
                  </h6>
                  {section.items && (
                    <ul className="analytics-info-section-list">
                      {section.items.map((item, i) => {
                        if (typeof item === "object" && item.text) {
                          return (
                            <li key={i}>
                              {item.text}
                              {item.subItems && item.subItems.length > 0 && (
                                <ul className="analytics-info-section-sublist" style={{ marginTop: 4, paddingLeft: 20, listStyleType: "circle" }}>
                                  {item.subItems.map((sub, j) => (
                                    <li key={j} style={{ marginBottom: 2 }}>{sub}</li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        }
                        return <li key={i}>{item}</li>;
                      })}
                    </ul>
                  )}
                </div>
              ))
            ) : (
              <>
                {metricInfo.definition && (
                  <div className="analytics-info-section">
                    <h6 className="analytics-info-section-heading">Metric Definition</h6>
                    <ul className="analytics-info-section-list">
                      <li>{metricInfo.definition}</li>
                    </ul>
                  </div>
                )}

                {metricInfo.calculation && (
                  <div className="analytics-info-section">
                    <h6 className="analytics-info-section-heading">Calculation Logic</h6>
                    <ul className="analytics-info-section-list">
                      <li>{metricInfo.calculation}</li>
                    </ul>
                  </div>
                )}

                {metricInfo.interpretation && (
                  <div className="analytics-info-section">
                    <h6 className="analytics-info-section-heading">Clinical Interpretation</h6>
                    <ul className="analytics-info-section-list">
                      <li>{metricInfo.interpretation}</li>
                    </ul>
                  </div>
                )}

                {metricInfo.exclusions && (
                  <div className="analytics-info-section">
                    <h6 className="analytics-info-section-heading">Exclusions & Rules</h6>
                    <ul className="analytics-info-section-list">
                      <li>{metricInfo.exclusions}</li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default ChartCard;
